import Button from '../Input2/Button';

export interface CallOutcomeOption {
  value: string;
  label: string;
}

/**
 * Toast copy shown after a "wrong number" disposition — the server removes the dialed number from
 * the contact so it isn't offered/dialed again. Shared so every surface (dashboard + extension
 * softphone and "Mark as done") gives the rep the same feedback.
 */
export const WRONG_NUMBER_REMOVED_TOAST = 'Wrong number removed from contact';

/**
 * The call dispositions offered in the UI, shared so the in-call softphone and the
 * call-step "Mark as done" picker render the same options. Listed alphabetically with
 * the catch-all "Other" pinned last. The server's `callOutcomeSchema` stays a superset
 * (it still accepts the retired `connected`/`callback` values) so already-deployed
 * extension builds sending them keep working.
 */
export const CALL_OUTCOMES: ReadonlyArray<CallOutcomeOption> = [
  { value: 'busy', label: 'Busy' },
  { value: 'no_answer', label: 'No answer' },
  { value: 'not_interested', label: 'Not interested' },
  { value: 'voicemail', label: 'Voicemail' },
  { value: 'wrong_number', label: 'Wrong number' },
  { value: 'other', label: 'Other' },
];

export interface CallOutcomePickerProps {
  /** The dispositions to offer. Defaults to {@link CALL_OUTCOMES}. */
  outcomes?: ReadonlyArray<CallOutcomeOption>;
  /** Prompt shown above the grid. */
  title?: string;
  onSelect: (value: string) => void;
  /** When provided, renders a "Skip" affordance below the grid. */
  onSkip?: () => void;
  /** Disable every button while a selection is being saved. */
  disabled?: boolean;
  className?: string;
}

/**
 * Pure "How did it go?" disposition grid. Shared by the in-call softphone (post-hangup) and the
 * call-step "Mark as done" popup so both log the same set of outcomes with identical styling.
 */
export function CallOutcomePicker({
  outcomes = CALL_OUTCOMES,
  title = 'How did it go?',
  onSelect,
  onSkip,
  disabled,
  className,
}: CallOutcomePickerProps) {
  return (
    <div className={className}>
      <span className="text-center text-sm text-gray-500 dark:text-gray-400">{title}</span>
      <div className="mt-2 grid grid-cols-2 gap-2">
        {outcomes.map((outcome) => (
          <Button
            key={outcome.value}
            size="sm"
            tone="outline"
            disabled={disabled}
            onClick={() => onSelect(outcome.value)}>
            {outcome.label}
          </Button>
        ))}
      </div>
      {onSkip && (
        <Button className="mt-2 w-full" size="sm" text disabled={disabled} onClick={onSkip}>
          Skip
        </Button>
      )}
    </div>
  );
}
